/**
 * Разбор очереди синхронизации.
 *
 * Синхронизация никогда не блокирует тренировку: любая её неудача остаётся
 * внутри этой функции и не доходит до экрана набора (ТЗ FR-SY-04, FR-SY-05).
 */

import type { EntitlementSnapshot, SyncId } from '@type2x/shared-contracts';

import type { SessionPayload, SubmitOutcome, SyncGateway } from '../contracts/gateway.js';
import type {
  DeliveryVerdict,
  FlushResult,
  QueuedOperation,
  SyncQueueRepository,
} from '../contracts/queue.js';
import type { SyncQueue } from './queue.js';

/** Путь отправки итога сессии. */
export const SESSIONS_PATH = '/sessions';

/** Итог одного прохода синхронизации. */
export interface SyncOutcome extends FlushResult {
  /** Снимок доступов, если сервер его вернул. */
  readonly entitlements: EntitlementSnapshot | null;
  /** Очередь не разобрана до конца из-за отсутствия аккаунта или связи. */
  readonly waiting: boolean;
}

/** Ставит итог сессии в очередь. */
export function enqueueSession(
  queue: SyncQueue,
  payload: SessionPayload,
  queuedAt: string,
): QueuedOperation {
  const operation: QueuedOperation = {
    syncId: payload.syncId as SyncId,
    path: SESSIONS_PATH,
    body: payload,
    queuedAt,
    attempts: 0,
  };
  queue.enqueue(operation);
  return operation;
}

/** Переводит ответ сервера в решение по операции. */
function verdictOf(outcome: SubmitOutcome): DeliveryVerdict {
  switch (outcome.kind) {
    case 'accepted':
      // Повтор считается доставкой: сервер уже сохранил этот результат.
      return 'delivered';
    case 'unauthenticated':
    case 'unavailable':
      return 'retry';
    case 'rejected':
      // Сервер отверг содержимое: повтор даст тот же ответ.
      return 'drop';
  }
}

/**
 * Разбирает очередь и обновляет снимок доступов.
 *
 * @param queue очередь в памяти;
 * @param repository хранилище очереди;
 * @param gateway порт доставки.
 */
export async function synchronise(
  queue: SyncQueue,
  repository: SyncQueueRepository,
  gateway: SyncGateway,
): Promise<SyncOutcome> {
  const result = await queue.flush(async (operation) =>
    verdictOf(await gateway.submitSession(operation.body as SessionPayload)),
  );

  await repository.save(queue.pending);

  // Доступы запрашиваются только когда очередь разобрана (ТЗ FR-SY-09): у
  // гостя аккаунта ещё нет, и лишний запрос вернёт отказ.
  const entitlements = queue.isEmpty ? await gateway.fetchEntitlements() : null;

  return { ...result, entitlements, waiting: !queue.isEmpty };
}

/** Восстанавливает очередь из хранилища. */
export async function restoreQueue(
  queue: SyncQueue,
  repository: SyncQueueRepository,
): Promise<void> {
  queue.restore(await repository.load());
}
